import { useCallback, useEffect, useState, type FormEvent } from 'react';
import { Hash, MessageSquare, Send, ShieldCheck, X } from 'lucide-react';
import { api, type Session } from '../api';
import type { SlackMessage } from '../../../src/connectors/ports';
import { Button } from '../components/ui/button';
import { useAgentName } from '../agent-name';

interface TextObject { text?: string }
interface SlackElement { type?: string; action_id?: string; value?: string; url?: string; text?: TextObject | string }
interface SlackBlock { type?: string; text?: TextObject; elements?: SlackElement[] }
interface ModalInput { block_id?: string; label?: TextObject; element?: { multiline?: boolean; initial_value?: string } }
interface ModalView { callback_id?: string; title?: TextObject; submit?: TextObject; blocks?: ModalInput[] }
interface Modal { triggerId: string; view: ModalView }
interface Feed { messages: SlackMessage[]; modals: Modal[]; channel: { id: string; name: string | null } | null }
interface OpenDialog extends Modal { values: Record<string, string> }
type ActionHandler = (message: SlackMessage, element: SlackElement) => void;

const feedPath = '/app-api/mock-slack';
const emptyFeed: Feed = { messages: [], modals: [], channel: null };

function elementText(element: SlackElement) {
  if (typeof element.text === 'string') return element.text;
  return element.text?.text ?? '';
}
function author(message: SlackMessage) {
  return message.user ? `@${message.user}` : 'Alder';
}
function initialValues(view: ModalView) {
  const values: Record<string, string> = {};
  for (const block of view.blocks ?? []) {
    if (block.block_id) values[block.block_id] = block.element?.initial_value ?? '';
  }
  return values;
}
function isThreadRoot(message: SlackMessage) {
  return !message.threadTs || message.threadTs === message.id;
}
function isReplyTo(message: SlackMessage, id: string) {
  return message.threadTs === id && message.id !== id;
}
function problemText(problem: unknown) {
  return problem instanceof Error ? problem.message : 'Please try again.';
}

function ActionButton({ element, busy, onAction }: { element: SlackElement; busy: boolean; onAction: () => void }) {
  const label = elementText(element);
  if (element.url) {
    return <a className="slack-link" href={element.url} target="_blank" rel="noreferrer">{label}</a>;
  }
  return <Button size="sm" variant="outline" disabled={busy} data-action-id={element.action_id} onClick={onAction}>{label}</Button>;
}

function BlockView({ block, busy, onAction }: { block: SlackBlock; busy: boolean; onAction: (element: SlackElement) => void }) {
  if (block.type === 'header') return <h3 className="slack-header">{block.text?.text}</h3>;
  if (block.type === 'section') return <p className="slack-section">{block.text?.text}</p>;
  const elements = block.elements ?? [];
  if (block.type === 'context') return <p className="muted text-sm slack-context">{elements.map(elementText).join(' ')}</p>;
  if (block.type !== 'actions') return null;
  return <div className="slack-actions">
    {elements.map((element, index) => (
      <ActionButton key={element.action_id ?? index} element={element} busy={busy} onAction={() => onAction(element)} />
    ))}
  </div>;
}

interface MessageViewProps { message: SlackMessage; replies: SlackMessage[]; busy: boolean; onAction: ActionHandler }
function MessageView({ message, replies, busy, onAction }: MessageViewProps) {
  const blocks = message.content.blocks as SlackBlock[];
  const body = blocks.length
    ? blocks.map((block, index) => <BlockView key={index} block={block} busy={busy} onAction={element => onAction(message, element)} />)
    : <p className="slack-section">{message.content.text}</p>;
  return <article className="slack-message" data-message-id={message.id} aria-label={`Message from ${author(message)}`}>
    <strong>{author(message)}</strong>
    {body}
    {replies.length > 0 && <div className="slack-thread" aria-label="Thread replies">
      {replies.map(reply => <MessageView key={reply.id} message={reply} replies={[]} busy={busy} onAction={onAction} />)}
    </div>}
  </article>;
}

interface DialogField { block: ModalInput; value: string; busy: boolean; onChange: (value: string) => void }
function DialogInput({ block, value, busy, onChange }: DialogField) {
  const id = `slack-${block.block_id ?? ''}`;
  const control = block.element?.multiline
    ? <textarea id={id} value={value} disabled={busy} rows={10} onChange={event => onChange(event.target.value)} />
    : <input id={id} value={value} disabled={busy} onChange={event => onChange(event.target.value)} />;
  return <label htmlFor={id}>{block.label?.text}{control}</label>;
}

interface EditDialogProps {
  dialog: OpenDialog;
  busy: boolean;
  onChange: (field: string, value: string) => void;
  onSubmit: () => void;
  onClose: () => void;
}
function EditDialog({ dialog, busy, onChange, onSubmit, onClose }: EditDialogProps) {
  const title = dialog.view.title?.text ?? 'Edit';
  const submit = (event: FormEvent) => {
    event.preventDefault();
    onSubmit();
  };
  return <div className="slack-dialog-backdrop">
    <form role="dialog" aria-modal="true" aria-label={title} className="slack-dialog" onSubmit={submit}>
      <header>
        <h2>{title}</h2>
        <Button type="button" variant="ghost" size="icon" aria-label="Close" onClick={onClose}><X aria-hidden /></Button>
      </header>
      {(dialog.view.blocks ?? []).map(block => {
        const field = block.block_id ?? '';
        const value = dialog.values[field] ?? '';
        return <DialogInput key={field} block={block} value={value} busy={busy} onChange={next => onChange(field, next)} />;
      })}
      <div className="editor-actions">
        <Button type="button" variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
        <Button type="submit" disabled={busy}>{dialog.view.submit?.text ?? 'Submit'}</Button>
      </div>
    </form>
  </div>;
}

/** A local Slack channel. Every message and click runs through the same handlers as the real Slack app. */
export function DemoSlack({ session }: { session: Session }) {
  const agent = useAgentName();
  const [feed, setFeed] = useState<Feed>(emptyFeed);
  const [text, setText] = useState('');
  const [thread, setThread] = useState<string | null>(null);
  const [dialog, setDialog] = useState<OpenDialog | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const isOwner = session.actor.roles.includes('owner');
  const channelName = feed.channel?.name ?? 'ar';
  const reload = useCallback(async () => {
    setFeed(await api<Feed>(feedPath));
  }, []);
  useEffect(() => {
    const refresh = () => {
      void reload().catch(problem => setError(problemText(problem)));
    };
    refresh();
    window.addEventListener('alder:state-changed', refresh);
    return () => window.removeEventListener('alder:state-changed', refresh);
  }, [reload]);

  async function run(work: () => Promise<void>) {
    setBusy(true);
    setError('');
    try {
      await work();
    } catch (problem) {
      setError(problemText(problem));
    } finally {
      setBusy(false);
      await reload().catch(() => undefined);
    }
  }
  const send = (event: FormEvent) => {
    event.preventDefault();
    if (!text.trim()) return;
    void run(async () => {
      const threadField = thread ? { threadTs: thread } : {};
      await api(feedPath, { kind: 'message', requestId: crypto.randomUUID(), text, ...threadField });
      setText('');
    });
  };
  const click: ActionHandler = (message, element) => {
    void run(async () => {
      const input = {
        kind: 'action',
        requestId: crypto.randomUUID(),
        messageId: message.id,
        actionId: element.action_id,
        value: element.value ?? '',
      };
      const result = await api<{ triggerId?: string }>(feedPath, input);
      if (!result.triggerId) return;
      const latest = await api<Feed>(feedPath);
      const opened = latest.modals.find(modal => modal.triggerId === result.triggerId);
      if (opened) setDialog({ ...opened, values: initialValues(opened.view) });
    });
  };
  const submitDialog = () => {
    if (!dialog) return;
    void run(async () => {
      const values = { subject: dialog.values.subject ?? '', body: dialog.values.body ?? '' };
      await api(feedPath, { kind: 'view_submission', requestId: crypto.randomUUID(), triggerId: dialog.triggerId, values });
      setDialog(null);
    });
  };
  const changeDialog = (field: string, value: string) => {
    if (dialog) setDialog({ ...dialog, values: { ...dialog.values, [field]: value } });
  };
  const roots = feed.messages.filter(isThreadRoot);
  const repliesTo = (id: string) => feed.messages.filter(message => isReplyTo(message, id));
  const canSend = !busy && Boolean(text.trim()) && Boolean(feed.channel);

  return <main id="main-content" className="queue-page slack-page">
    <div className="demo-mail-note">
      <ShieldCheck size={17} aria-hidden />
      <span><strong>Local demo Slack</strong> · Messages and clicks stay in this workspace.</span>
    </div>
    <header className="page-heading"><div>
      <span className="eyebrow">SUPERVISE FROM SLACK</span>
      <h1><Hash size={26} aria-hidden />{channelName}<span className="heading-dot">.</span></h1>
      <p>Ask {agent} about receivables and review cards exactly as your team would in Slack.</p>
    </div></header>
    {error && <div className="alert error" role="alert">{error}</div>}
    {!feed.channel && <p className="empty-copy">Connect the demo Slack workspace in Slack setup first.</p>}
    <section className="slack-feed" aria-label="Channel messages" aria-live="polite">
      {roots.map(message => <div key={message.id}>
        <MessageView message={message} replies={repliesTo(message.id)} busy={busy || !isOwner} onAction={click} />
        {isOwner && <Button variant="ghost" size="sm" onClick={() => setThread(message.id)}>
          <MessageSquare aria-hidden />Reply in thread
        </Button>}
      </div>)}
    </section>
    {isOwner
      ? <form className="slack-composer" onSubmit={send}>
        {thread && <p className="muted text-sm">
          Replying in thread <Button type="button" variant="ghost" size="sm" onClick={() => setThread(null)}>Back to channel</Button>
        </p>}
        <label htmlFor="slack-input">Message #{channelName}</label>
        <input
          id="slack-input"
          value={text}
          onChange={event => setText(event.target.value)}
          disabled={busy || !feed.channel}
          placeholder="Ask about a customer, invoice or what is due"
          maxLength={4000}
        />
        <Button type="submit" disabled={!canSend}><Send aria-hidden />Send</Button>
      </form>
      : <p className="muted">Only the workspace owner can post in the demo channel.</p>}
    {dialog && <EditDialog dialog={dialog} busy={busy} onChange={changeDialog} onSubmit={submitDialog} onClose={() => setDialog(null)} />}
  </main>;
}
